import {
  Calendar,
  DatePicker,
  DatePickerChangeEvent,
  DatePickerProps,
} from '@progress/kendo-react-dateinputs';
import { IntlProvider, load } from '@progress/kendo-react-intl';
import { Popup } from '@progress/kendo-react-popup';
import weekData from 'cldr-core/supplemental/weekData.json';
import roCaGeneric from 'cldr-dates-full/main/ro/ca-generic.json';
import roDateFields from 'cldr-dates-full/main/ro/dateFields.json';
import _ from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { CustomerInfoContext } from '../../apps/benefit/contexts/CustomerInfoContext';
import { ValidationError } from '../../apps/benefit/models/Errors';
import roCaGregorian from '../../common/assets/calendar/ro/ca-gregorian.json';
import { config } from '../global/config';
import ErrorViewer from './ErrorViewer';

load(roCaGregorian, roDateFields, roCaGeneric, weekData);

interface IMonthPickerProps {
  controlClassName?: string;
  mandatory?: boolean;
  value: Date | null;
  label?: string;
  errors?: ValidationError[];
  onChange?: (e: DatePickerChangeEvent) => void;
  onClear?: () => void;
  clearErrors?: () => void;
  placeholder?: string;
}

export default function MonthPicker(
  props: IMonthPickerProps & Omit<DatePickerProps, 'label'>,
) {
  const { customerInfo } = useContext(CustomerInfoContext);
  const datePickerRef = useRef<DatePicker>(null);
  // Track calendar state to force re-renders when it opens/closes
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const { value, label, errors, controlClassName, id, mandatory, onClear } =
    props;
  const currentLanguage = customerInfo?.languageCode ?? config.defaultLanguage;

  // Monitor if the calendar popup is open
  useEffect(() => {
    const checkPopupState = () => {
      const popup = document.querySelector('.k-animation-container');
      const isOpen = !!popup && popup.querySelector('.k-calendar') !== null;
      setIsCalendarOpen(isOpen);
    };

    checkPopupState();

    const observer = new MutationObserver(checkPopupState);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Use isCalendarOpen to satisfy linter (forces re-render on state change)
  useEffect(() => {
    // This effect intentionally uses isCalendarOpen to ensure re-renders
  }, [isCalendarOpen]);

  const internalOnChange = (e: DatePickerChangeEvent) => {
    if (props.errors?.length) {
      props.clearErrors?.();
    }
    props.onChange?.(e);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.errors?.length) {
      props.clearErrors?.();
    }
    props.onClear?.();
  };

  const handleInputClick = () => {
    // Find the calendar toggle button
    const toggleButton = datePickerRef.current?.element?.querySelector(
      '.k-input-button',
    ) as HTMLButtonElement;

    if (toggleButton) {
      toggleButton.click();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Open calendar on Enter or Space key
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleInputClick();
    }
  };

  const customCalendar = (props: any) => {
    return (
      <Calendar
        {...props}
        value={props.value}
        bottomView="year"
        defaultActiveView="year"
        onChange={props.onChange}
      />
    );
  };

  const customPopup = (props: any) => {
    return (
      <Popup
        {...props}
        animate={true}
        appendTo={document.body}
        anchorAlign={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        popupAlign={{
          horizontal: 'center',
          vertical: 'top',
        }}
      />
    );
  };

  const classNames = `control-group ${controlClassName ?? ''}`.trim();

  return (
    <div className={classNames}>
      <div className="control-content">
        {!!label && (
          <label htmlFor={id}>
            {label}
            {mandatory && <sup>*</sup>}
          </label>
        )}
        <div
          className="month-picker-container"
          style={{ position: 'relative' }}
        >
          <IntlProvider locale={currentLanguage ?? 'ro'}>
            <DatePicker
              {..._.omit(props, 'label', 'onClear')}
              ref={datePickerRef}
              value={value}
              focusedDate={value ?? undefined}
              format="MMM yyyy"
              popup={customPopup}
              placeholder={props.placeholder}
              calendar={customCalendar}
              onChange={internalOnChange}
            />
          </IntlProvider>
          {/* Overlay to capture clicks on the input */}
          <button
            type="button"
            className="month-picker-overlay"
            onClick={handleInputClick}
            onKeyDown={handleKeyDown}
            aria-label="Open month picker"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: '40px',
              bottom: 0,
              cursor: 'pointer',
              zIndex: 1,
              border: 'none',
              background: 'transparent',
              padding: 0,
            }}
          />
          {value && onClear && (
            <button
              type="button"
              onClick={handleClear}
              className="month-picker-clear"
              style={{ position: 'absolute', zIndex: 3 }}
            ></button>
          )}
        </div>
      </div>
      <ErrorViewer errors={errors} />
    </div>
  );
}
